/**
 * PortalDashboard — one page of everything the auditee owes and everything
 * that was formally sent to them. Plain human language only: no auditor
 * vocabulary anywhere (a test asserts it).
 */
import React from 'react';
import AuditeeRequestList from '../Audit/AuditeeRequestList.jsx';
import { CORRESPONDENCE_KIND_LABELS } from '../Audit/labels.js';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');
const fmtSize = (b) => (b > 0 ? `${Math.max(1, Math.round(b / 1024))} KB` : '');

function SectionLabel({ children }) {
  return <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">{children}</div>;
}

export default function PortalDashboard({ view, adapter, onChanged }) {
  const audit = view.audit || {};
  const requests = view.open_requests || [];
  const openCount = requests.filter((r) => !r.responded_at).length;
  const correspondence = view.correspondence || [];
  const uploads = view.uploads || [];
  const attempts = view.universe_attempts;

  return (
    <div className="space-y-6">
      {/* Header — who this is, who they're working with, the dates that matter. */}
      <section>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Your audit page</div>
        <h1 className="text-xl font-bold text-slate-800 leading-tight">{audit.subject_name}</h1>
        {audit.template_name && <div className="text-sm text-slate-500">{audit.template_name}</div>}
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          You're working with the Compliance team on this audit. Everything they need from you is listed below,
          and everything they've formally sent you is recorded here.
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 mt-2">
          {audit.engagement_date && <span>Started <b>{fmtDate(audit.engagement_date)}</b></span>}
          {audit.evidence_due_date && <span>Documents due <b>{fmtDate(audit.evidence_due_date)}</b></span>}
        </div>
      </section>

      {/* What you owe */}
      <section>
        <SectionLabel>
          What you owe{openCount > 0 ? ` · ${openCount} need${openCount === 1 ? 's' : ''} your answer` : ''}
        </SectionLabel>
        <AuditeeRequestList requests={requests} uploads={uploads} adapter={adapter} onChanged={onChanged} />
      </section>

      {/* Quiet CMS note on universe attempts (FDR contacts only). */}
      {attempts && attempts.count > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[12px] text-slate-600">
          Universe submission attempt <b>{attempts.count}</b> of 3. After the third attempt CMS cites an Invalid Data Submission.
        </div>
      )}

      {/* What you've received */}
      <section>
        <SectionLabel>What you've received</SectionLabel>
        {correspondence.length === 0 ? (
          <div className="text-xs text-slate-400 bg-white border border-slate-200 rounded-xl px-4 py-4">Nothing has been sent to you yet.</div>
        ) : (
          <div className="space-y-2">
            {correspondence.map((c, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl px-4 py-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-orange-50 text-orange-700 border border-orange-100 rounded px-1.5 py-0.5">
                    {CORRESPONDENCE_KIND_LABELS[c.kind] || 'Message'}
                  </span>
                  <span className="text-[10px] text-slate-400">{fmtDate(c.sent_at)}</span>
                </div>
                {c.subject && <div className="text-[13px] font-semibold text-slate-700 mt-0.5">{c.subject}</div>}
                {c.attachment_name && <div className="text-[11px] text-slate-500">Attached: {c.attachment_name}</div>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Your submissions */}
      <section>
        <SectionLabel>Your submissions</SectionLabel>
        {uploads.length === 0 ? (
          <div className="text-xs text-slate-400 bg-white border border-slate-200 rounded-xl px-4 py-4">You haven't uploaded anything yet.</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-2 divide-y divide-slate-100">
            {uploads.map((u) => (
              <div key={u.id} className="py-2 flex items-center gap-2 text-[12px]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                <span className="text-slate-700 font-medium truncate">{u.file_name}</span>
                <span className="text-slate-400 flex-shrink-0 ml-auto">{fmtSize(u.file_size_bytes)} · {fmtDate(u.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <p className="text-[11px] text-slate-400 pt-2 border-t border-slate-200">
        Your answers and uploads go straight to the audit team the moment you send them, and every action here is recorded.
      </p>
    </div>
  );
}
